// hooks/useApiConnection.js
// Replaces useHardhatConnection — fetches dashboard data from the Express API
// instead of directly querying the blockchain. Ethers.js is still used for
// wallet connect and write operations (cancel, refund) elsewhere in the app.

import { useState, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const EMPTY_STATE = {
  customers: [],
  metrics: { mrr: 0, totalCustomers: 0, activeSubscriptions: 0, churnRate: 0, lifetimeValue: 0, growthRate: 0 },
  recentActivity: [],
  loading: false,
  connected: false,
  error: null,
};

export function useApiConnection(network, merchantAddress, forceReload = 0) {
  const [state, setState] = useState({ ...EMPTY_STATE, loading: true });
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const sigRef = useRef({ networkId: null, subsAddress: null, merchant: null });

  // Periodic refresh: 5s for local Hardhat, 30s for public networks.
  useEffect(() => {
    const ms = network.id === 'hardhat' ? 5000 : 30000;
    const id = setInterval(() => setLastUpdate(Date.now()), ms);
    return () => clearInterval(id);
  }, [network.id]);

  useEffect(() => {
    if (!network.subsAddress || !merchantAddress) {
      setState({ ...EMPTY_STATE });
      return;
    }

    // Only show full-page loading spinner when network or merchant actually changes.
    const prev = sigRef.current;
    const changed =
      prev.networkId !== network.id ||
      prev.subsAddress !== network.subsAddress ||
      prev.merchant !== merchantAddress;
    sigRef.current = { networkId: network.id, subsAddress: network.subsAddress, merchant: merchantAddress };
    if (changed) setState(p => ({ ...p, loading: true, connected: false, error: null }));

    let cancelled = false;

    async function fetchAll() {
      const params = `merchant=${encodeURIComponent(merchantAddress)}&network=${encodeURIComponent(network.id)}`;

      try {
        const [subsRes, metricsRes, activityRes] = await Promise.all([
          fetch(`${API_URL}/api/subscriptions?${params}`),
          fetch(`${API_URL}/api/metrics?${params}`),
          fetch(`${API_URL}/api/activity?${params}&limit=100`),
        ]);

        if (!subsRes.ok || !metricsRes.ok || !activityRes.ok) {
          throw new Error(`API error: ${[subsRes, metricsRes, activityRes].find(r => !r.ok)?.status}`);
        }

        const [customers, metrics, recentActivity] = await Promise.all([
          subsRes.json(),
          metricsRes.json(),
          activityRes.json(),
        ]);

        console.log('[useApiConnection] success', { customers: customers.length, recentActivity: recentActivity.length, metrics });
        if (!cancelled) {
          setState({ customers, metrics, recentActivity, loading: false, connected: true, error: null });
        }
      } catch (err) {
        console.error('[useApiConnection] fetch error:', err.message, err);
        if (!cancelled) {
          setState(p => ({
            ...p,
            loading: false,
            connected: false,
            error: err.message.includes('fetch') || err.message.includes('Failed')
              ? `Cannot connect to API server. Is it running at ${API_URL}?`
              : err.message,
          }));
        }
      }
    }

    console.log('[useApiConnection] calling fetchAll', { API_URL, merchantAddress, networkId: network.id });
    fetchAll();
    return () => { cancelled = true; };
  }, [network.id, network.subsAddress, merchantAddress, lastUpdate, forceReload]);

  return state;
}
